import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
import {
  FLOATING_MAP_INITIAL_POSITION,
  FLOATING_MAP_SIZE_PRESETS,
} from "./floatingRouteMapLayout.js";

export const FLEET_FLOATING_ROUTE_MAP_STORAGE_KEY = "grain-saas:fleet-floating-route-map:v1";

const STORAGE_VERSION = 1;

function resolveFloatingRouteMapStorage(storage) {
  if (storage && typeof storage.getItem === "function" && typeof storage.setItem === "function") {
    return storage;
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    return userUiPreferenceStorage("local");
  } catch {
    return null;
  }
}

function resolveNumericPosition(value, fallback) {
  const resolvedValue = Number(value);
  return Number.isFinite(resolvedValue) ? resolvedValue : fallback;
}

function resolvePersistedSize(value, sizePresets = FLOATING_MAP_SIZE_PRESETS) {
  const normalizedValue = typeof value === "string" ? value.trim() : "";
  if (normalizedValue && sizePresets[normalizedValue]) {
    return normalizedValue;
  }

  if (sizePresets.compact) {
    return "compact";
  }

  if (sizePresets.standard) {
    return "standard";
  }

  return Object.keys(sizePresets)[0] || "compact";
}

function resolveOptionalDimension(value) {
  if (value == null) {
    return null;
  }

  const resolvedValue = Number(value);
  return Number.isFinite(resolvedValue) ? resolvedValue : null;
}

export function readPersistedFloatingRouteMapState(
  storageKey = FLEET_FLOATING_ROUTE_MAP_STORAGE_KEY,
  {
    sizePresets = FLOATING_MAP_SIZE_PRESETS,
    storage = null,
  } = {},
) {
  const resolvedStorage = resolveFloatingRouteMapStorage(storage);
  if (!resolvedStorage || !storageKey) {
    return null;
  }

  try {
    const rawValue = resolvedStorage.getItem(storageKey);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || typeof parsedValue !== "object" || parsedValue.version !== STORAGE_VERSION) {
      return null;
    }

    if (parsedValue.isOpen !== true) {
      return null;
    }

    return {
      customHeight: resolveOptionalDimension(parsedValue.customHeight),
      customWidth: resolveOptionalDimension(parsedValue.customWidth),
      hasCustomPosition: parsedValue.hasCustomPosition === true,
      isOpen: true,
      position: {
        x: resolveNumericPosition(parsedValue.position?.x, FLOATING_MAP_INITIAL_POSITION.x),
        y: resolveNumericPosition(parsedValue.position?.y, FLOATING_MAP_INITIAL_POSITION.y),
      },
      size: resolvePersistedSize(parsedValue.size, sizePresets),
    };
  } catch {
    return null;
  }
}

export function persistFloatingRouteMapState(
  snapshot,
  storageKey = FLEET_FLOATING_ROUTE_MAP_STORAGE_KEY,
  storage = null,
) {
  const resolvedStorage = resolveFloatingRouteMapStorage(storage);
  if (!resolvedStorage || !storageKey || snapshot?.isOpen !== true) {
    return null;
  }

  const nextSnapshot = {
    customHeight: resolveOptionalDimension(snapshot.customHeight),
    customWidth: resolveOptionalDimension(snapshot.customWidth),
    hasCustomPosition: snapshot.hasCustomPosition === true,
    isOpen: true,
    position: {
      x: resolveNumericPosition(snapshot.position?.x, FLOATING_MAP_INITIAL_POSITION.x),
      y: resolveNumericPosition(snapshot.position?.y, FLOATING_MAP_INITIAL_POSITION.y),
    },
    size: resolvePersistedSize(snapshot.size),
    version: STORAGE_VERSION,
  };

  try {
    resolvedStorage.setItem(storageKey, JSON.stringify(nextSnapshot));
    return nextSnapshot;
  } catch {
    return null;
  }
}

export function clearPersistedFloatingRouteMapState(
  storageKey = FLEET_FLOATING_ROUTE_MAP_STORAGE_KEY,
  storage = null,
) {
  const resolvedStorage = resolveFloatingRouteMapStorage(storage);
  if (!resolvedStorage || !storageKey) {
    return;
  }

  try {
    resolvedStorage.removeItem(storageKey);
  } catch {
    // Ignore storage failures in restricted browser contexts.
  }
}
